import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const LightingMoodSection = () => {
  const [selectedMood, setSelectedMood] = useState('all');

  const moodCategories = [
  { id: 'all', name: 'All Moods', icon: 'Sparkles' },
  { id: 'morning', name: 'Morning Bright', icon: 'Sun' },
  { id: 'afternoon', name: 'Afternoon Warm', icon: 'Sunset' },
  { id: 'evening', name: 'Evening Cozy', icon: 'Moon' },
  { id: 'night', name: 'Night Vibrant', icon: 'Stars' }];


  const moodImages = [
  {
    id: 1,
    mood: 'morning',
    title: 'Fresh Morning Start',
    image: "https://images.unsplash.com/photo-1471772669640-4e87fd440c6d",
    imageAlt: 'Bright restaurant interior in morning light with natural sunlight streaming through windows, clean tables, and fresh breakfast setup',
    time: '8:00 AM - 11:00 AM',
    description: 'Natural light floods the space, perfect for breakfast tacos'
  },
  {
    id: 2,
    mood: 'afternoon',
    title: 'Golden Hour Glow',
    image: "https://images.unsplash.com/photo-1648040779073-494407342c6b",
    imageAlt: 'Restaurant dining area bathed in warm afternoon golden light with amber tones, comfortable seating, and inviting atmosphere',
    time: '12:00 PM - 4:00 PM',
    description: 'Warm amber tones create appetite-stimulating ambience'
  },
  {
    id: 3,
    mood: 'evening',
    title: 'Cozy Evening Vibes',
    image: "https://images.unsplash.com/photo-1578474846511-04ba529f0b88",
    imageAlt: 'Restaurant interior with soft evening lighting, warm pendant lights, intimate table settings, and cozy atmosphere for dinner',
    time: '5:00 PM - 8:00 PM',
    description: 'Soft lighting perfect for dinner with friends and family'
  },
  {
    id: 4,
    mood: 'night',
    title: 'Vibrant Night Energy',
    image: "https://images.unsplash.com/photo-1637754844217-3c8136999263",
    imageAlt: 'Lively restaurant at night with colorful accent lighting, energetic atmosphere, bustling dining area, and vibrant social scene',
    time: '8:00 PM - 11:00 PM',
    description: 'Energetic atmosphere with accent lighting and social buzz'
  },
  {
    id: 5,
    mood: 'morning',
    title: 'Breakfast Corner',
    image: "https://images.unsplash.com/photo-1509353067293-61a1010c7867",
    imageAlt: 'Bright breakfast corner with morning sunlight, fresh coffee setup, clean counters, and welcoming morning ambience',
    time: '7:00 AM - 10:00 AM',
    description: 'Bright and welcoming space to start your day right'
  },
  {
    id: 6,
    mood: 'afternoon',
    title: 'Lunch Rush Warmth',
    image: "https://images.unsplash.com/photo-1615017018194-692efef9010a",
    imageAlt: 'Busy restaurant during lunch with warm lighting, active dining area, food service in progress, and welcoming daytime atmosphere',
    time: '12:00 PM - 3:00 PM',
    description: 'Bustling energy with comfortable warm lighting'
  },
  {
    id: 7,
    mood: 'evening',
    title: 'Dinner Intimacy',
    image: "https://images.unsplash.com/photo-1647296020397-a79cd5127090",
    imageAlt: 'Intimate dinner setting with soft mood lighting, candlelit tables, romantic ambience, and elegant evening atmosphere',
    time: '6:00 PM - 9:00 PM',
    description: 'Intimate setting with carefully balanced mood lighting'
  },
  {
    id: 8,
    mood: 'night',
    title: 'Late Night Hangout',
    image: "https://images.unsplash.com/photo-1728661478147-31d9ed9c639f",
    imageAlt: 'Late night restaurant scene with vibrant lighting, social gathering atmosphere, lively crowd, and energetic nighttime vibe',
    time: '9:00 PM - 12:00 AM',
    description: 'Perfect lighting for late-night conversations and tacos'
  }];


  const filteredImages = selectedMood === 'all' ?
  moodImages :
  moodImages?.filter((img) => img?.mood === selectedMood);

  return (
    <section className="py-16 bg-[var(--color-card)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            Lighting & Mood
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
            Experience how our ambience transforms throughout the day
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {moodCategories?.map((category) =>
          <button
            key={category?.id}
            onClick={() => setSelectedMood(category?.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
            selectedMood === category?.id ?
            'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] shadow-lg scale-105' :
            'bg-[var(--color-background)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]'}`
            }>

              <Icon name={category?.icon} size={18} color="currentColor" />
              <span>{category?.name}</span>
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredImages?.map((item, index) =>
          <div
            key={item?.id}
            className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 animate-slide-up bg-[var(--color-background)]"
            style={{ animationDelay: `${index * 100}ms` }}>

              <div className="aspect-[3/4] relative overflow-hidden">
                <Image
                src={item?.image}
                alt={item?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />

                
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Clock" size={16} color="#FFFFFF" />
                    <span className="text-xs text-white font-medium">{item?.time}</span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-2">
                    {item?.title}
                  </h3>
                  
                  <p className="text-sm text-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                    {item?.description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredImages?.length === 0 &&
        <div className="text-center py-12">
            <Icon name="ImageOff" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-[var(--color-muted-foreground)]">No images found for this mood</p>
          </div>
        }
      </div>
    </section>);

};

export default LightingMoodSection;